import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import Box from "@mui/material/Box"
import ButtonGroup from "@mui/material/ButtonGroup"
import Chip from "@mui/material/Chip"
import IconButton from "@mui/material/IconButton"
import Stack from "@mui/material/Stack"
import Tooltip from "@mui/material/Tooltip"

import { StickyTableCell } from "../../../common/components/tables/cells"
import { formatPhone } from "../../../common/utils"

/** Fixed width so the name sticky column can offset past the checkbox. */
export const NAME_CHECKBOX_COL_WIDTH = 48

export const stickyCheckboxCellSx = {
  left: 0,
  width: NAME_CHECKBOX_COL_WIDTH,
  minWidth: NAME_CHECKBOX_COL_WIDTH,
  maxWidth: NAME_CHECKBOX_COL_WIDTH,
  px: 0.5,
  zIndex: 4,
}

export const stickyNameCellSx = {
  left: NAME_CHECKBOX_COL_WIDTH,
  minWidth: 280,
}

const actionButtonSx = {
  borderColor: "colors.brand200",
  "&:hover": {
    bgcolor: "colors.brand100",
  },
}

/**
 * Sticky name cell with right-aligned Call / SMS / Email actions.
 * Always shows Call → SMS → Email in that order when `showActions` is true;
 * disables individual actions when phone / email is missing.
 */
export default function NameWithContactActionsCell({
  name,
  title,
  phone,
  email,
  onCall,
  onSMS,
  onEmail,
  showActions = true,
  isDnc = false,
  sx,
}) {
  const phoneText = phoneDisplay(phone)
  const hasPhone = Boolean(phoneText)
  const hasEmail = Boolean(email?.trim())
  const callDisabled = !hasPhone || isDnc
  const emailDisabled = !hasEmail || isDnc
  const dncTitle =
    "This lead is on the DNC list and is not eligible for call, SMS, or email"

  return (
    <StickyTableCell
      title={title ?? name}
      sx={{
        ...stickyNameCellSx,
        ...(showActions ? null : { minWidth: 120 }),
        ...sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ width: "100%" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: isDnc ? 700 : 400,
              color: isDnc ? "error.dark" : "inherit",
            }}
          >
            {name}
          </Box>
          {isDnc ? (
            <Chip
              size="small"
              color="error"
              label="DNC"
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
          ) : null}
        </Stack>
        {showActions ? (
          <ButtonGroup
            variant="outlined"
            size="small"
            sx={{
              bgcolor: isDnc ? "rgba(211, 47, 47, 0.08)" : "colors.brand50",
              borderRadius: "8px",
              borderColor: isDnc ? "error.light" : "colors.brand200",
              flexShrink: 0,
              ml: "auto",
            }}
          >
            <Tooltip
              title={isDnc ? dncTitle : hasPhone ? "Call" : "No phone number"}
            >
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={callDisabled}
                  onClick={onCall}
                  sx={actionButtonSx}
                >
                  <PhoneOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={isDnc ? dncTitle : hasPhone ? "SMS" : "No phone number"}
            >
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={callDisabled}
                  onClick={onSMS}
                  sx={actionButtonSx}
                >
                  <SmsOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isDnc ? dncTitle : hasEmail ? "Email" : "No email"}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={emailDisabled}
                  onClick={onEmail}
                  sx={actionButtonSx}
                >
                  <EmailOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </ButtonGroup>
        ) : null}
      </Stack>
    </StickyTableCell>
  )
}

export function phoneDisplay(phone) {
  if (!phone || phone === "+10000000000") {
    return ""
  }
  return formatPhone(phone)
}

/**
 * Dial waterfall for MLS + uploaded leads: Mobile -> Direct -> Preferred.
 * Office and home numbers are intentionally excluded so cold calls never land
 * on a brokerage's main line; a lead whose only number is an office line
 * resolves to "" and the Call action disables instead of dialing the office.
 */
export function leadPhoneValue(lead) {
  if (!lead) {
    return ""
  }
  for (const value of [
    lead.verified_phone,
    lead.phone, // RESO MemberMobilePhone
    lead.member_directphone,
    lead.member_preferred_phone,
  ]) {
    if (
      value &&
      String(value).trim() &&
      String(value).trim() !== "+10000000000"
    ) {
      return String(value).trim()
    }
  }
  return ""
}
